import { ApiRequest, ShowLoader } from 'GlobalFunctions';
import { useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import SubscriptionForm from 'components/Checkout/Utils/SubscriptionForm';

// Subscription popup HTML structure
const subscriptionPopupHTML = `
<div id="subscription_popup" class="subscription-popup" style="display: none;">
  <div class="subscription-popup-inner">
   <h2>Template 7</h2>

    <div class='subscription-due'>
        <div>
            <div class='due_label'>Due today</div>
            <div class='due_price' id='summary_price'>$75.00</div>
        </div>

        <div>
            <div class='sp_label_1'>
            <span id='recurrent_amount'>$0.00</span> USD per month afterwards</div>
            <div class='sp_next_payment'>Next payment 26 February, 2025</div>   
        </div>
    </div>

    <div id="subscription_payment_form"></div>
  </div>
</div>
`;

/**
 * Handles closing the subscription popup
 * Can be triggered by click events outside the popup or called directly
 * @param {MouseEvent} [event] Optional click event
 */
const handleClose = (event?: MouseEvent) => {
  const popup = document.getElementById("subscription_popup");
  
  // If event is provided, only close when clicking outside the inner container
  if (event) {
    const popupInner = document.querySelector(".subscription-popup-inner");
    if (popup && popupInner && !popupInner.contains(event.target as Node)) {
      popup.style.display = "none";
      // Remove the event listener when closing the popup
      document.removeEventListener('click', handleClose);
    }
  } else {
    // Direct call - close immediately
    if (popup) {
      popup.style.display = "none";
      // Remove the event listener when closing the popup
      document.removeEventListener('click', handleClose);
    }
  }
};

/**
 * Check if the current product is a subscription type
 * @returns {boolean} True if the product is a subscription
 */
export const checkForSubscription = () => {
    const productTypeEl = document.querySelector("[pc='product-type']");
    if (productTypeEl && productTypeEl.textContent?.trim().toLowerCase() === "subscription") {
        // Check if subscription popup already exists
        if (!document.getElementById("subscription_popup")) {
            // Create and append the subscription popup with the predefined HTML structure
            const tempContainer = document.createElement('div');
            tempContainer.innerHTML = subscriptionPopupHTML;
            
            // Get the first child (our popup div) and append it to the body
            const subscriptionPopupElement = tempContainer.firstElementChild;
            if (subscriptionPopupElement) {
                document.body.appendChild(subscriptionPopupElement);
                console.log("Added subscription popup for subscription product");

                // Transform the billing-cycle element into a <select> with <option>s.
                const billingCycleEl = document.querySelector("[pc='billing-cycle']");
                if (billingCycleEl && billingCycleEl.textContent) {
                    const cycles = billingCycleEl.textContent.split(",").map(str => str.trim());
                    const selectEl = document.createElement("select");
                    selectEl.id = "billing_cycle_select"; // Add ID for easy access
                    cycles.forEach(cycle => {
                        const [timePart, costPart] = cycle.split(":");
                        const qty = parseInt(timePart.slice(0, -1), 10);
                        const unit = timePart.slice(-1);
                        let label = "";
                        switch (unit) {
                            case "y": label = qty === 1 ? "Annually" : `${qty} years`; break;
                            case "m": label = qty === 1 ? "Monthly" : `${qty} months`; break;
                            case "w": label = qty === 1 ? "Weekly" : `${qty} weeks`; break;
                            case "d": label = qty === 1 ? "Daily" : `${qty} days`; break;
                        }
                        const option = document.createElement("option");
                        option.value = cycle;
                        option.text = `${label} ($${costPart})`;
                        selectEl.appendChild(option);
                    });
                    billingCycleEl.innerHTML = "";
                    billingCycleEl.appendChild(selectEl);

                    // Preselect first item and update price
                    if (cycles.length > 0) {
                        selectEl.selectedIndex = 0;
                        const [, firstCostPart] = cycles[0].split(":");
                        const firstCostNum = parseFloat(firstCostPart).toFixed(2);
                        const summaryPrice = document.getElementById("summary_price");
                        const recurrentAmount = document.getElementById("recurrent_amount");
                        if (summaryPrice) summaryPrice.textContent = `$${firstCostNum}`;
                        if (recurrentAmount) recurrentAmount.textContent = `$${firstCostNum}`;
                        const productPriceEl = document.querySelector("[pc='product-price']");
                        if (productPriceEl) productPriceEl.textContent = `${firstCostNum}`;
                    }

                    // Add event listener to update price
                    selectEl.addEventListener("change", () => {
                        const [ , costPart] = selectEl.value.split(":");
                        const costNum = parseFloat(costPart).toFixed(2);
                        const summaryPrice = document.getElementById("summary_price");
                        const recurrentAmount = document.getElementById("recurrent_amount");
                        if (summaryPrice) summaryPrice.textContent = `$${costNum}`;
                        if (recurrentAmount) recurrentAmount.textContent = `$${costNum}`;

                        // Also update product-price
                        const productPriceEl = document.querySelector("[pc='product-price']");
                        if (productPriceEl) productPriceEl.textContent = `${costNum}`;
                    });
                }

                // Add event listeners for the popup
                const closeButton = document.querySelector(".subscription-close-btn");
                const cancelButton = document.getElementById("cancel_subscription");
                
                if (closeButton) {
                    closeButton.addEventListener("click", () => {
                        const popup = document.getElementById("subscription_popup");
                        if (popup) {
                            popup.style.display = "none";
                        }
                    });
                }
                
                if (cancelButton) {
                    cancelButton.addEventListener("click", () => {
                        const popup = document.getElementById("subscription_popup");
                        if (popup) {
                            popup.style.display = "none";
                        }
                    });
                }
            }
        }
        return true;
    }
    return false;
};

/**
 * Starts the subscription process for a product
 * @param {any} productData Product data for subscription
 */
export const startSubscription = async (productData: any) => {
    console.log("Starting subscription for Template 7", productData);
    
    // Make sure the subscription popup exists
    if (!document.getElementById("subscription_popup")) {
        // Insert the predefined HTML structure
        const tempContainer = document.createElement('div');
        tempContainer.innerHTML = subscriptionPopupHTML;
        const subscriptionPopupElement = tempContainer.firstElementChild;
        if (subscriptionPopupElement) {
            document.body.appendChild(subscriptionPopupElement);
        }
    }
    
    // Show the popup
    const popup = document.getElementById("subscription_popup");
    if (popup) {
        popup.style.display = "flex";
        
        // Add event listener for closing when clicking outside
        // Use setTimeout to avoid immediate triggering when the popup opens
        setTimeout(() => {
            document.addEventListener('click', handleClose);
        }, 100);
        
        // Update summary with product data
        const summaryPrice = document.getElementById("summary_price");
        if (summaryPrice && productData.price) {
            summaryPrice.textContent = `$${parseFloat(productData.price).toFixed(2)}`;
        }
        
        const summaryTotal = document.getElementById("recurrent_amount");
        if (summaryTotal && productData.price) {
            summaryTotal.textContent = `$${parseFloat(productData.price).toFixed(2)}`;
        }

        // Get the selected billing cycle and add it to productData
        const billingCycleSelect = document.getElementById("billing_cycle_select") as HTMLSelectElement;
        if (billingCycleSelect) {
            productData.billing_cycle = billingCycleSelect.value;
        }

        // Setup form submission
        const subscriptionForm = document.getElementById("subscription_form") as HTMLFormElement;
        if (subscriptionForm) {
            subscriptionForm.addEventListener("submit", (e) => {
                e.preventDefault();
                console.log("Subscription form submitted", {
                    ...productData,
                    subscription: {
                        plan: (document.getElementById("subscription_plan") as HTMLSelectElement)?.value,
                        quantity: (document.getElementById("subscription_quantity") as HTMLInputElement)?.value,
                        delivery_date: (document.getElementById("delivery_date") as HTMLInputElement)?.value
                    }
                });
                popup.style.display = "none";
            });
        }
        await getPaymentForm(productData);
    }
};

/*===============================================
GET PAYMENT FORM
===============================================*/
export const getPaymentForm = async (additionalData: any) => {
    ShowLoader(true);
    await ApiRequest({
        url: "/payments/getSubscriptionPaymentForm",
        method: "get",
        query: {
            order_id: localStorage.getItem('order_id'),
        },
        setResponse: async (response: any) => {
            console.log("Payment form response", response);
            ShowLoader(false);
            if(response.status==='error') {
                alert(response.message);
                return;
            }
            renderPaymentForm(response,additionalData)
        },
    });
};

/*===============================================
 RENDER PAYMENT FORM
===============================================*/
const renderPaymentForm = (paymentInfo: any, additionalData: any) => {
    if (Object.keys(paymentInfo).length === 0) return;
    
    const subscriptionForm = document.getElementById("subscription_payment_form");
    if (subscriptionForm) {
        const root = createRoot(subscriptionForm);
        
        const setPaymentStatus = (status: string, message: string) => {
            // Minimal example
            alert(`${status}: ${message}`);
        };
        
        root.render(
            <>
                {paymentInfo.payment_method === 'elavon' ? (
                    <SubscriptionForm
                        payment_method={paymentInfo.payment_method}
                        setPaymentStatus={setPaymentStatus}
                        ShowLoader={ShowLoader}
                        is_subscription={true}
                        additionalData={additionalData}
                        handleClose={handleClose}
                    />
                ) : (
                    <div className="unavailable-payment">Unavailable payment method</div>
                )}
            </>
        );
    }
};

/**
 * Cleanup subscription-related DOM elements
 */
export const cleanupSubscription = () => {
    // Remove event listener when cleaning up
    document.removeEventListener('click', handleClose);
    
    const subscriptionPopup = document.getElementById("subscription_popup");
    if (subscriptionPopup) {
        try {
            document.body.removeChild(subscriptionPopup);
        } catch (error) {
            console.warn('Error removing subscription popup:', error);
        }
    }
};
